import type { Definition, Objective } from '../api/missions';

/**
 * The execution of a mission as phases, derived and never authored (UI doc,
 * decisions): phase 1 is the insertion, the middle phases are the objective
 * graph's own dependency depth, the last is the extraction. Nothing edits a
 * phase; what changes the phases is the graph.
 *
 * An objective's depth is one more than the deepest objective it depends on;
 * a dependency on an id the graph does not have, or a cycle, counts as none,
 * so a broken graph still reads as a list instead of an empty card.
 */
export interface Phase {
  /** 'insertion', 'objectives' or 'extraction'. */
  kind: 'insertion' | 'objectives' | 'extraction';
  objectives: Objective[];
}

export function objectiveDepths(objectives: Objective[]): Map<string, number> {
  const byId = new Map(objectives.map((o) => [o.id, o]));
  const depth = new Map<string, number>();
  const visiting = new Set<string>();
  // -1 marks a dependency met again while still being resolved: a cycle, which
  // the caller skips instead of counting, so the pair reads 0 and 1, not 1 and 2.
  const of = (id: string): number => {
    const known = depth.get(id);
    if (known !== undefined) return known;
    if (visiting.has(id)) return -1;
    visiting.add(id);
    const o = byId.get(id);
    let d = 0;
    for (const dep of o?.dependsOn ?? []) {
      if (!byId.has(dep)) continue;
      const dd = of(dep);
      if (dd >= 0) d = Math.max(d, dd + 1);
    }
    visiting.delete(id);
    depth.set(id, d);
    return d;
  };
  objectives.forEach((o) => of(o.id));
  return depth;
}

export function derivePhases(def: Pick<Definition, 'objectives' | 'points'>): Phase[] {
  const depths = objectiveDepths(def.objectives);
  const levels = new Map<number, Objective[]>();
  for (const o of def.objectives) {
    const d = depths.get(o.id) ?? 0;
    levels.set(d, [...(levels.get(d) ?? []), o]);
  }
  const middle = [...levels.keys()]
    .sort((a, b) => a - b)
    .map((d) => ({
      kind: 'objectives' as const,
      objectives: levels.get(d) ?? [],
    }));
  return [{ kind: 'insertion', objectives: [] }, ...middle, { kind: 'extraction', objectives: [] }];
}
